/**
 * Copyright (c) 2023-present,
 * Eunoia Digital, S.L. All rights reserved.
 */

import { AxiosError, AxiosResponse } from 'axios';
import configuration from 'config';
import { ConfiguracionMiembroResponse } from 'types/interfaces/idConfiguracionMiembro';
import ConfiguracionMiembro from 'types/models/configuracionMiembrosMatcher';
import apiService from './ApiService';

interface Estado {
  linea: number;
  status: string;
  mensaje: string;
}

interface ResponseData {
  estados: Estado[];
}

export interface PostConfiguracionMiembro {
  idInscrito: string;
  idPerfil: number;
  consumoAnual: number;
  betaMax: number;
  betaMin: number;
  miembroPreferente: string;
  miembroVip: string;
  seleccionado: string;
}

export interface PatchConfiguracionMiembro {
  idInscrito?: string;
  idConfiguracion?: number;
  idPerfil?: number;
  consumoAnual?: number;
  betaMax?: number;
  betaMin?: number;
  miembroPreferente?: string;
  miembroVip?: string;
  seleccionado?: string;
  aceptado?: string;
}

export class MatcherConfiguracionMiembrosService {
  private readonly http = apiService;

  private apiUrl = configuration.apiUrl;

  public async getAllConfiguracionesMiembros(id: Number): Promise<Array<ConfiguracionMiembro>> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/`;

    const response: AxiosResponse = await this.http.get(url);
    const perfiles = new ConfiguracionMiembro().deserializeArray(response.data.datos);
    return perfiles;
  }

  public async getConfiguracionMiembroById(id: Number, id_miembro: Number): Promise<ConfiguracionMiembro> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/${id_miembro}`;

    const response: AxiosResponse = await this.http.get(url);
    return new ConfiguracionMiembro().deserialize(response.data.datos);
  }

  async postConfiguracionMiembro(
    id: Number,
    newConfiguracionMiembro: PostConfiguracionMiembro,
  ): Promise<ConfiguracionMiembroResponse> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/`;

    const response: AxiosResponse<ConfiguracionMiembroResponse> = await this.http.post(url, newConfiguracionMiembro);

    return response.data;
  }

  async patchConfiguracionMiembro(
    id: Number,
    id_miembro: Number,
    configuracionMiembroActualizacion: PatchConfiguracionMiembro,
  ): Promise<string> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/${id_miembro}/?nocache=${new Date(
      Date.now(),
    )}`;

    const response: AxiosResponse<string> = await this.http.patch(url, configuracionMiembroActualizacion);

    return response.data;
  }

  async deleteConfiguracionMiembro(id: Number, id_miembro: Number): Promise<string> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/${id_miembro}`;

    const response: AxiosResponse<string> = await this.http.delete(url);

    return response.data;
  }

  async subirCsvConfiguracionMatcherMiembros(id: number, id_miembro: number, file: File): Promise<any> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/${id_miembro}/curva-consumo`;
    const csvData = new window.FormData();
    csvData.append('curvaDeConsumo', file);
    let responsePromise;
    try {
      const response: AxiosResponse = await this.http.patch(url, csvData);
      responsePromise = response.status;
    } catch (error: unknown) {
      if ((error as AxiosError)?.response?.data && (error as AxiosError).response?.status === 400) {
        responsePromise = {
          codigo: (error as AxiosError).response?.data[0].codigo,
          mensaje: (error as AxiosError).response?.data[0].mensaje,
        };
      }
    }
    return responsePromise;
  }

  async loadCsvConfiguracionMatcherMiembros(id: number, file: File): Promise<ResponseData> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/subir/`;
    const listaInscritos = new window.FormData();
    listaInscritos.append('listaInscritos', file);
    let responsePromise: ResponseData = { estados: [] };

    const response: AxiosResponse = await this.http.post(url, listaInscritos);
    responsePromise = response.data.datos;

    return responsePromise;
  }

  // Devuelve un CSV (representado por Blob en JavaScript)
  public async descargarInforme(id: number, id_miembro: number): Promise<Blob> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/${id_miembro}/curva-consumo`;
    const response: AxiosResponse<Blob> = await this.http.get(url);

    return response.data;
  }

  async loadMiembrosComunidad(id: number): Promise<number> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/importar-inscritos/`;
    const response: AxiosResponse<{ status: number }> = await this.http.post(url);

    return response.status;
  }

  async loadMiembrosConsumo(id: number): Promise<number> {
    const url = `${this.apiUrl}/matcher/configuracion-matcher/${id}/miembro/cargar-consumo/`;
    const response: AxiosResponse<{ status: number }> = await this.http.post(url);

    return response.status;
  }
}

const MatcherConfiguracionMiembrosInstance = new MatcherConfiguracionMiembrosService();
export default MatcherConfiguracionMiembrosInstance;
